import { Row, Col, Input, Button, Segmented, Dropdown } from "antd";
import {
  CheckSquare,
  Filter,
  ChevronLeft,
  Menu,
  ChevronRight,
  Search as LucideSearch,
  Settings,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { iconMap } from "./AddEditEvent";

const { Search } = Input;

const TopCalendarHeader = ({
  currentDate = dayjs(),
  goPrev,
  goNext,
  goToday,
  searchParams,
  onFilterChange,
  changeView,
  isSidebarOpen,
  setSidebarOpen,
}) => {
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);
  const type = searchParams.get("type");
  const navigate = useNavigate();
  const location = useLocation();
  const calendarView = searchParams.get("calendarView");
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");

  useEffect(() => {
    setKeyword(searchParams.get("keyword") || "");
  }, [location.search]);

  const debouncedNavigate = useMemo(
    () =>
      debounce((val) => {
        const params = new URLSearchParams(window.location.search);
        if (val && val.trim() !== "" && val !== "all")
          params.set("keyword", val);
        else params.delete("keyword");

        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }, 500),
    [location.pathname],
  );

  const handleInputChange = (e) => {
    const val = e.target.value;
    setKeyword(val);
    debouncedNavigate(val);
  };

  const date =
    currentDate.format("MMMM YYYY") === "Invalid Date" ? dayjs() : currentDate;

  return (
    <div className="tw-mb-6 tw-w-full">
      <Row
        gutter={[16, 16]}
        className="tw-w-full tw-flex tw-items-center tw-justify-between"
      >
        <Col className="tw-flex tw-items-center tw-gap-3">
            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-h-[38px] tw-w-[38px] tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-xl tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-cursor-pointer tw-transition-colors"
              onClick={() => setSidebarOpen(!isSidebarOpen)}
            >
              <Menu size={16} />
            </button>

          <h4 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0">
            {date.format("MMMM YYYY")}
          </h4>
          
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <button
              onClick={goPrev}
              type="button"
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-transition-all tw-duration-150 tw-cursor-pointer"
            >
              <ChevronLeft size={16} />
            </button>
            <Button
              onClick={goToday}
              className="!tw-h-8 !tw-rounded-lg !tw-border-slate-200 !tw-bg-white hover:!tw-border-slate-300 !tw-px-3 !tw-text-xs !tw-font-semibold !tw-text-slate-600 hover:!tw-text-slate-800 tw-transition-all tw-duration-200"
            >
              Today
            </Button>
            <button
              onClick={goNext}
              type="button"
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-transition-all tw-duration-150 tw-cursor-pointer"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </Col>
        
        <Col className="tw-flex tw-items-center tw-gap-2.5">
          <Search
            placeholder="Search events..."
            allowClear
            value={keyword}
            onClear={() => onFilterChange("keyword", null)}
            onInput={handleInputChange}
            className="calendar-event-search"
            prefix={<LucideSearch size={16} />}
          />

          <Dropdown
            menu={{
              items: [
                {
                  key: "all",
                  value: "all",
                  label: (
                    <div
                      className={`tw-flex tw-items-center tw-justify-between tw-gap-2.5 ${!type ? "tw-text-theme" : "tw-text-slate-700"}`}
                    >
                      All{" "}
                      <span className="tw-mx-2.5">
                        <CheckSquare size={16} />
                      </span>
                    </div>
                  ),
                  onClick: () => onFilterChange("type", null),
                },
                ...eventTypes
                  ?.filter((i) =>
                    ["Birthday", "Anniversary", "Meeting"]?.includes(
                      i?.typeName,
                    ),
                  )
                  ?.map((d) => ({
                    key: d?.calendarEventTypeId,
                    label: (
                      <div
                        className={`tw-flex tw-items-center tw-justify-between tw-gap-2.5 ${d?.calendarEventTypeId === parseInt(type) ? "tw-text-theme" : "tw-text-slate-700"}`}
                      >
                        {d?.typeName}{" "}
                        <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-mx-2.5">
                          {iconMap[d?.typeName]}
                        </span>
                      </div>
                    ),
                    value: d?.calendarEventTypeId,
                    onClick: () =>
                      onFilterChange("type", d?.calendarEventTypeId),
                  })),
                {
                  key: "tasks",
                  label: "Tasks",
                  children: eventTypes
                    ?.filter(
                      (i) =>
                        !["Birthday", "Anniversary", "Meeting"]?.includes(
                          i?.typeName,
                        ),
                    )
                    ?.map((d) => ({
                      key: d?.calendarEventTypeId,
                      label: (
                        <div
                          className={`tw-flex tw-items-center tw-justify-between tw-gap-2.5 ${d?.calendarEventTypeId === parseInt(type) ? "tw-text-theme" : "tw-text-slate-700"}`}
                        >
                          {d?.typeName}{" "}
                          <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-mx-2.5">
                            {iconMap[d?.typeName]}
                          </span>
                        </div>
                      ),
                      value: d?.calendarEventTypeId,
                      onClick: () =>
                        onFilterChange("type", d?.calendarEventTypeId),
                    })),
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomLeft"
          >
            <Filter size={18} className="tw-flex tw-items-center tw-justify-center tw-h-[38px] tw-w-[38px] tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-500 hover:tw-text-slate-700 tw-rounded-full tw-cursor-pointer tw-transition-all tw-duration-200 tw-p-2" />
          </Dropdown>
        </Col>

        <Col className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3 tw-w-full">
            <Segmented
              size="middle"
              className="calendar-segmented"
              value={calendarView ? calendarView : "dayGridMonth"}
              onChange={(value) => changeView(value)}
              options={[
                { label: "Month", value: "dayGridMonth" },
                { label: "Week", value: "timeGridWeek" },
                { label: "Day", value: "timeGridDay" },
              ]}
            />

            <Link
              to="/leads-timeline/availability?tab=1"
              className="tw-flex tw-h-[38px] tw-w-[38px] tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-transition-colors"
            >
              <Settings size={18} />
            </Link>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TopCalendarHeader;
